<template>
  <VueIfBot>
    <transition name="slide-fade">
      <div v-if="shouldShowConsent" class="cookie-consent" :class="{ agentgrid: isAgentGrid }" role="dialog">
        <p class="text">
          <strong>We only have to do this once!</strong>
          We use cookies to give you the best experience. To learn more about our cookies, check out our
          <a
            href="https://www.animaapp.com/tos/TOS.pdf#page=9"
            target="_blank"
            rel="noopener noreferrer"
            class="privacy-link"
          >
            Privacy Policy
          </a>
        </p>

        <div class="buttons">
          <button class="btn btn-accept" @click="accept">Accept all</button>
          <button class="btn btn-reject" @click="reject">Reject non-essential cookies</button>
        </div>
      </div>
    </transition>
  </VueIfBot>
</template>

<script setup>
import VueIfBot from 'vue-if-bot';
import { useStore } from '@/store';
import { computed } from 'vue';
import { useRoute } from 'vue-router/composables';
import { trackEvent } from '@/services/tracking';
import { isAgentGridSource } from '@/utils/authBrand';

const { dispatch, getters } = useStore();
const route = useRoute();
const shouldShowConsent = computed(() => getters['consent/shouldShowConsent']);
// The banner is app-wide, so it follows the same Agent Grid detection as the auth views.
const isAgentGrid = computed(() => isAgentGridSource(route?.query));

const accept = () => {
  // The choice first: tracking is gated on the cookie this writes, so
  // recording the accept beforehand would drop the very event we want.
  dispatch('consent/grantConsent');
  trackEvent('consent-banner.accept.click');
};

const reject = () => {
  dispatch('consent/denyConsent');

  // Deliberately after the choice, so the consent gate drops it: someone who
  // declined should not have their decline reported. The call is kept so the
  // intent stays visible, and rejections remain countable in aggregate as page
  // views minus accepts.
  trackEvent('consent-banner.reject.click');
};
</script>

<style lang="scss" scoped>
@import '@/styles/_auth-agentgrid-tokens.scss';

.cookie-consent {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 16px;
  padding: 0 24px;
  height: 76px;
  background-color: #1e1e1e;
  z-index: 9999;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    height: auto;
    padding: 16px;
    gap: 12px;
  }
}

.text {
  flex: 1;
  font-size: 14px;
  line-height: 1.5;
  color: #d1d5db;
  margin: 0;

  strong {
    color: white;
  }
}

.privacy-link {
  color: #a78bfa;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

.buttons {
  display: flex;
  gap: 8px;
  flex-shrink: 0;

  @media (max-width: 768px) {
    width: 100%;
  }
}

.btn {
  height: 32px;
  padding: 0 20px;
  font-size: 14px;
  font-weight: 500;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  @media (max-width: 768px) {
    flex: 1;
  }
}

.btn-accept {
  background-color: #6d54cf;
  color: white;

  &:hover {
    background-color: rgba(#6d54cf, 0.8);
  }
}

.btn-reject {
  background-color: transparent;
  color: #9ca3af;
  border: 1px solid rgba(white, 0.1);

  &:hover {
    background-color: rgba(white, 0.1);
    color: #d1d5db;
  }
}

// ============================================================================
// Agent Grid branding — flows started from Agent Grid get its light theme
// (see src/utils/authBrand.js). Palette lives in _auth-agentgrid.scss.
// ============================================================================

.cookie-consent.agentgrid {
  background-color: var(--agentgrid-background-surface);
  border-top: 1px solid var(--agentgrid-border-default);
  font-family: $agentgrid-body-font;

  .text {
    font-family: $agentgrid-body-font;
    font-size: $agentgrid-text-size;
    line-height: 18px;
    color: var(--agentgrid-text-secondary);

    strong {
      font-weight: 500;
      color: var(--agentgrid-text-primary);
    }
  }

  .privacy-link {
    font-weight: 500;
    color: var(--agentgrid-text-primary);
  }

  .btn {
    height: $agentgrid-control-height;
    padding: 0 12px;
    border-radius: $agentgrid-radius-button;
    font-family: $agentgrid-body-font;
    font-size: $agentgrid-text-size;
    font-weight: 500;
    line-height: 100%;
  }

  .btn-accept {
    background-color: var(--agentgrid-action-primary);
    border: 1px solid var(--agentgrid-action-primary-border);
    color: var(--agentgrid-text-inverse);

    &:hover {
      background-color: var(--agentgrid-action-primary-hover);
    }

    &:active {
      background-color: var(--agentgrid-action-primary-pressed);
    }
  }

  .btn-reject {
    background-color: var(--agentgrid-background-surface);
    border: 1px solid var(--agentgrid-border-strong);
    color: var(--agentgrid-text-primary);

    &:hover {
      background-color: var(--agentgrid-background-default);
      border-color: var(--agentgrid-border-hover);
      color: var(--agentgrid-text-primary);
    }
  }
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(100%);
  opacity: 0;
}
</style>
